// ═══════════════════════════════════════════════════════════════
// Incident Hub — Create / Edit Incident
// Modal-style form for all incident fields
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Title, Paper, Group, Button, TextInput, Textarea, Select, Text, Badge,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconArrowLeft, IconMapPin, IconRoadSign, IconBuilding } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useIncidentStore } from '../../store/incidentStore';
import type { IncidentType, IncidentStatus } from '../../types';
import { WORKER_URL } from '../../lib/api-switch';

const WORKER = WORKER_URL;
const HEADERS = { 'Origin': 'https://apps.opengov.com' };

interface HereSuggestion {
  id: string;
  title: string;
  resultType?: string; // 'place' | 'houseNumber' | 'street' | 'intersection' | 'locality' | ...
  address?: { label?: string };
  position?: { lat: number; lng: number };
}

const TYPE_OPTIONS = [
  { value: 'natural_disaster', label: 'Natural Disaster' },
  { value: 'mci', label: 'Mass Casualty Incident' },
  { value: 'hazmat', label: 'Hazardous Materials' },
  { value: 'exercise', label: 'Exercise / Drill' },
  { value: 'training', label: 'Training' },
  { value: 'special_event', label: 'Special Event' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'closed', label: 'Closed' },
];

export default function IncidentCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { incidents } = useAuthStore();
  const { createIncident, updateIncident, isLoading } = useIncidentStore();

  const [name, setName] = useState('');
  const [incidentType, setIncidentType] = useState<string>('natural_disaster');
  const [status, setStatus] = useState<string>('active');
  const [location, setLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [description, setDescription] = useState('');
  const [commander, setCommander] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());

  // Address autocomplete (HERE Autosuggest — supports intersections)
  const [addrSuggestions, setAddrSuggestions] = useState<HereSuggestion[]>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const addrTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddressSuggestions = useCallback((query: string) => {
    if (addrTimerRef.current) clearTimeout(addrTimerRef.current);
    if (query.trim().length < 3) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    addrTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${WORKER}/api/here/autosuggest?q=${encodeURIComponent(query)}&at=33.749,-84.388&limit=8`,
          { headers: HEADERS },
        );
        const data = await res.json() as { items?: HereSuggestion[] };
        const items = (data.items ?? []).filter(i => i.address?.label || i.title);
        setAddrSuggestions(items);
        setAddrOpen(items.length > 0);
      } catch {
        setAddrSuggestions([]);
        setAddrOpen(false);
      }
    }, 250);
  }, []);

  const pickAddress = async (s: HereSuggestion) => {
    const label = s.address?.label || s.title;
    setLocation(label);
    setAddrOpen(false);
    setAddrSuggestions([]);
    if (s.position) {
      setLatitude(String(s.position.lat));
      setLongitude(String(s.position.lng));
      return;
    }
    // Some street/locality suggestions don't include a position — call lookup
    // by suggestion id to fetch it. Falls back silently if it fails.
    try {
      const lookup = await fetch(
        `${WORKER}/api/here/lookup?id=${encodeURIComponent(s.id)}`,
        { headers: HEADERS },
      );
      if (lookup.ok) {
        const data = await lookup.json() as { position?: { lat: number; lng: number } };
        if (data.position) {
          setLatitude(String(data.position.lat));
          setLongitude(String(data.position.lng));
        }
      }
    } catch { /* ignore — user can still set lat/lng manually */ }
  };

  // Load existing incident data for edit mode
  useEffect(() => {
    if (isEdit && id) {
      const inc = incidents.find(i => String(i.id) === id);
      if (inc) {
        setName(inc.name);
        setIncidentType(inc.incidentType);
        setStatus(inc.status);
        setLocation(inc.location);
        setLatitude(inc.latitude || '');
        setLongitude(inc.longitude || '');
        setDescription(inc.description);
        setCommander(inc.commander);
        setStartDate(inc.startDate ? new Date(inc.startDate) : null);
      }
    }
  }, [isEdit, id, incidents]);

  // Show a clear error if the user is trying to edit an incident that
  // doesn't exist (bad URL, deleted record, or incidents list not loaded yet)
  const editTargetMissing = isEdit && id && incidents.length > 0 && !incidents.find(i => String(i.id) === id);
  if (editTargetMissing) {
    return (
      <div>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/incidents')}>Back</Button>
            <Title order={3} c="black">Incident Not Found</Title>
          </Group>
        </Group>
        <Paper p="lg" radius="sm" withBorder>
          <Text size="sm" mb="md">
            Incident #{id} doesn't exist or you don't have permission to view it.
            It may have been deleted, or the URL may be incorrect.
          </Text>
          <Button onClick={() => navigate('/incidents')}>Back to Incident List</Button>
        </Paper>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      notifications.show({ message: 'Incident name is required', color: 'red' });
      return;
    }
    const dateStr = startDate ? startDate.toISOString().split('T')[0] : '';
    try {
      if (isEdit && id) {
        await updateIncident(Number(id), {
          name: name.trim(),
          incidentType: incidentType as IncidentType,
          status: status as IncidentStatus,
          location: location.trim(),
          latitude: latitude.trim(),
          longitude: longitude.trim(),
          description: description.trim(),
          commander: commander.trim(),
          startDate: dateStr,
        });
        notifications.show({ message: 'Incident updated', color: 'green' });
        navigate(`/incidents/${id}`);
      } else {
        const rid = await createIncident({
          name: name.trim(),
          incidentType: incidentType as IncidentType,
          status: status as IncidentStatus,
          location: location.trim(),
          latitude: latitude.trim(),
          longitude: longitude.trim(),
          description: description.trim(),
          commander: commander.trim(),
          startDate: dateStr,
        });
        notifications.show({ message: 'Incident created', color: 'green' });
        navigate(`/incidents/${rid}`);
      }
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to save incident', color: 'red' });
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(isEdit ? `/incidents/${id}` : '/incidents')}>Back</Button>
          <Title order={3} c="black">{isEdit ? 'Edit Incident' : 'New Incident'}</Title>
        </Group>
      </Group>

      <Paper p="lg" radius="sm" withBorder>
        <TextInput label="Incident Name" required placeholder="e.g. Hurricane Milton Response"
          value={name} onChange={e => setName(e.currentTarget.value)} mb="sm" />

        <Group grow mb="sm">
          <Select label="Type" data={TYPE_OPTIONS} value={incidentType}
            onChange={v => setIncidentType(v ?? 'other')} />
          <Select label="Status" data={STATUS_OPTIONS} value={status}
            onChange={v => setStatus(v ?? 'active')} />
        </Group>

        <TextInput label="Incident Commander" placeholder="Name of IC"
          value={commander} onChange={e => setCommander(e.currentTarget.value)} mb="sm" />

        <div style={{ position: 'relative' }}>
          <TextInput
            label="Location"
            placeholder='Address, place, or intersection (e.g. "Peachtree St & 10th Ave NE")'
            value={location}
            onChange={e => {
              setLocation(e.currentTarget.value);
              fetchAddressSuggestions(e.currentTarget.value);
            }}
            onFocus={() => { if (addrSuggestions.length > 0) setAddrOpen(true); }}
            onBlur={() => { setTimeout(() => setAddrOpen(false), 200); }}
            description={
              latitude && longitude
                ? `Coordinates resolved: ${parseFloat(latitude).toFixed(5)}, ${parseFloat(longitude).toFixed(5)}`
                : 'Type at least 3 characters. Intersections like "Main & 1st" are supported.'
            }
            mb="sm"
          />
          {addrOpen && addrSuggestions.length > 0 && (
            <Paper withBorder shadow="md" style={{
              position: 'absolute', top: 'calc(100% - 18px)', left: 0, right: 0,
              zIndex: 50, maxHeight: 280, overflowY: 'auto',
            }}>
              {addrSuggestions.map(s => {
                const isIntersection = s.resultType === 'intersection';
                const isPlace = s.resultType === 'place';
                const Icon = isIntersection ? IconRoadSign : isPlace ? IconBuilding : IconMapPin;
                return (
                  <div
                    key={s.id}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                    }}
                    onMouseDown={e => { e.preventDefault(); pickAddress(s); }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}
                  >
                    <Icon size={14} style={{ marginTop: 2, color: isIntersection ? '#dc2626' : isPlace ? '#0891b2' : '#6b7280' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" lineClamp={1}>{s.title}</Text>
                      {s.address?.label && s.address.label !== s.title && (
                        <Text size="xs" c="dimmed" lineClamp={1}>{s.address.label}</Text>
                      )}
                    </div>
                    {s.resultType && (
                      <Badge size="xs" variant="light"
                        color={isIntersection ? 'red' : isPlace ? 'cyan' : 'gray'}>
                        {s.resultType}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </Paper>
          )}
        </div>

        <Group grow mb="sm">
          <TextInput label="Latitude" placeholder="e.g. 28.5383"
            value={latitude} onChange={e => setLatitude(e.currentTarget.value)} />
          <TextInput label="Longitude" placeholder="e.g. -81.3792"
            value={longitude} onChange={e => setLongitude(e.currentTarget.value)} />
        </Group>

        <DateInput label="Start Date" placeholder="Pick a date"
          value={startDate} onChange={(v) => setStartDate(v ? new Date(v) : null)} clearable mb="sm" />

        <Textarea label="Description" placeholder="Describe the incident, scope, objectives..."
          value={description} onChange={e => setDescription(e.currentTarget.value)}
          minRows={4} mb="lg" />

        <Group justify="flex-end">
          <Button variant="light" onClick={() => navigate(isEdit ? `/incidents/${id}` : '/incidents')}>Cancel</Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            {isEdit ? 'Save Changes' : 'Create Incident'}
          </Button>
        </Group>
      </Paper>
    </div>
  );
}
